// libs
import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
// config & functions
import ajaxCall from '../../utils/ajaxcall';
import config from '../../config';

function getApiMessage(apiCall) {
    const status = apiCall?.context?.status ?? apiCall?.context?.statusCode;
    const rawMessage = apiCall?.context?.message;
    const body = rawMessage?.data ?? rawMessage;
    const serverMessage = typeof body === 'string'
        ? body
        : (body?.message || body?.error || body?.detail || '');

    if (serverMessage && serverMessage !== 'Failed to import inventory from Excel') {
        return serverMessage;
    }

    switch (Number(status)) {
        case 400:
            return 'The workbook is invalid. Check the required columns, file format, and row values, then try again.';
        case 401:
            return 'Your session has expired. Sign in again, then retry the upload.';
        case 403:
            return 'You do not have access to this seller account. Select an account you are allowed to manage.';
        case 413:
            return 'The workbook is too large for the server. Split it into smaller files and upload again.';
        case 415:
            return 'This file format is not supported. Choose an .xlsx, .xls, or .csv file.';
        case 422:
            return serverMessage || 'The workbook contains invalid values. Correct the reported rows and retry.';
        case 500:
            return serverMessage || 'The server could not finish the import. Check the SKU-width migration and API logs, then retry the same file.';
        case 503:
            return serverMessage || 'The API is unavailable. Check that the SAS API is running, then retry.';
        default:
            return serverMessage || 'The upload failed. Check the file and API connection, then retry.';
    }
}

function UploadModal({ setShowUploadModal, loadapidata, apidataparams, urlaccountid, token }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [uploadError, setUploadError] = useState('');

    const hideModal = () => {
        setShowUploadModal(false);
    };

    const handleDownloadTemplate = async () => {
        if (!urlaccountid) {
            toast.error('Please choose a seller account first');
            return;
        }
        try {
            const response = await ajaxCall.downloadCall(`${config.api.generic}/inventory/status?template=1&accountId=${urlaccountid}`, token);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'inventory_import_template.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                toast.error('Template API not found on server. Redeploy sas-ecommerce-api and try again.');
            } else if (status === 401 || status === 403) {
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error('Failed to download template');
            }
        }
    };

    const handleDownloadInventory = async () => {
        if (!urlaccountid) {
            toast.error('Please choose a seller account first');
            return;
        }
        try {
            const response = await ajaxCall.downloadCall(`${config.api.generic}/inventory/export?accountId=${urlaccountid}`, token);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'inventory_export.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                toast.error('Export API not found on server. Redeploy sas-ecommerce-api and try again.');
            } else if (status === 401 || status === 403) {
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error('Failed to download inventory');
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError('Choose an Excel or CSV file before uploading.');
            return;
        }
        if (!urlaccountid) {
            setUploadError('Choose a seller account before uploading.');
            return;
        }

        setIsUploading(true);
        setImportResult(null);
        setUploadError('');
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('accountId', urlaccountid);

            const apiCall = await ajaxCall.postCall(`${config.api.generic}/inventory`, formData, token);
            if (apiCall && apiCall.status) {
                const result = apiCall.context.message.data;
                setImportResult(result);
                const rowErrorCount = result?.errors?.length || 0;
                const changedCount = (result?.variantsCreated || 0) + (result?.variantsUpdated || 0);
                if (changedCount > 0) {
                    toast.success(`Created ${result.variantsCreated || 0} and updated ${result.variantsUpdated || 0} variant(s)`);
                    loadapidata(apidataparams);
                }
                if (rowErrorCount > 0) {
                    toast.warning(`Import finished with ${rowErrorCount} row error(s)`);
                } else if (!changedCount) {
                    toast.info('No rows were created or updated. Check the result details below.');
                } else {
                    setUploadError('');
                }
            } else {
                const message = getApiMessage(apiCall);
                setUploadError(message);
                toast.error(message);
            }
        } catch (err) {
            const status = err?.response?.status;
            const message = getApiMessage({
                context: {
                    status,
                    message: err?.response?.data || (err?.request
                        ? 'Unable to reach the API. Check the server and network connection.'
                        : err?.message),
                },
            });
            setUploadError(message);
            toast.error(message);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <Modal show={true} onHide={hideModal} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-18rem">Upload Inventory (Excel)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="fs-14rem text-muted">
                        Upload an Excel sheet to bulk-create or bulk-update products and variants for the selected seller account.
                        Download the current inventory, edit rows in place (its row-id columns keep updates matched to the right records), then upload the same file to save changes.
                        Download the blank template to get dropdown values for status, category, color, and size from your database when adding new rows.
                        Use the Specification Keys tab for valid specification keys (for example tipShape, not Tip shape).
                    </p>
                    <div className="mb-3 d-flex gap-2">
                        <Button variant="outline-primary" size="sm" onClick={handleDownloadInventory}>
                            Download Inventory
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={handleDownloadTemplate}>
                            Download Template
                        </Button>
                    </div>
                    <Row>
                        <Col md="12">
                            <label className="form-label fs-14rem fw-bold">Excel File (.xlsx, .xls, .csv)</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                                onChange={(e) => {
                                    setSelectedFile(e.target.files?.[0] || null);
                                    setImportResult(null);
                                    setUploadError('');
                                }}
                                disabled={isUploading}
                            />
                            {selectedFile && (
                                <small className="form-text text-muted d-block mt-1">
                                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                                </small>
                            )}
                        </Col>
                    </Row>
                    {uploadError && (
                        <div className="alert alert-danger mt-3 mb-0" role="alert" aria-live="assertive">
                            <strong>Upload failed</strong>
                            <div>{uploadError}</div>
                            {selectedFile && <small className="d-block mt-1">Your file is still selected. Correct the issue if needed, then retry.</small>}
                        </div>
                    )}
                    <div className="mt-3">
                        <p className="fs-13rem fw-bold mb-2">Required columns</p>
                        <p className="fs-12rem text-muted mb-0">
                            product_name, sku_id, quantity, mrp, listing_price
                        </p>
                        <p className="fs-13rem fw-bold mb-2 mt-3">Optional columns</p>
                        <p className="fs-12rem text-muted mb-0">
                            product_status, category, description, product_image, specification, meta_config, color, size_label, size_unit, variant_status
                        </p>
                        <p className="fs-12rem text-muted mb-0 mt-2">
                            specification example: {`[{"key":"tipShape","value":"Cotton"}]`} — copy keys from the Specification Keys tab.
                        </p>
                        <p className="fs-12rem text-muted mb-0 mt-2">
                            category example: {`KTM > Duke 390`} for a sub-category, or just {`KTM`} for a top-level category.
                        </p>
                    </div>
                    {importResult && (
                        <div className="mt-4">
                            <div className={`alert ${importResult.errors?.length ? 'alert-warning' : 'alert-success'}`} role="status" aria-live="polite">
                                <p className="mb-1 fs-14rem"><strong>Import finished</strong></p>
                                <p className="mb-1 fs-14rem"><strong>Products created:</strong> {importResult.productsCreated || 0}</p>
                                <p className="mb-1 fs-14rem"><strong>Products updated:</strong> {importResult.productsUpdated || 0}</p>
                                <p className="mb-1 fs-14rem"><strong>Variants created:</strong> {importResult.variantsCreated || 0}</p>
                                <p className="mb-1 fs-14rem"><strong>Variants updated:</strong> {importResult.variantsUpdated || 0}</p>
                                <p className="mb-0 fs-14rem"><strong>Rows with errors:</strong> {importResult.errors?.length || 0}</p>
                            </div>
                            {importResult.errors?.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-sm fs-13rem">
                                        <thead>
                                            <tr>
                                                <th scope="col">Excel row</th>
                                                <th scope="col">Problem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importResult.errors.map((item, index) => (
                                                <tr key={`import-error-${index}`}>
                                                    <td>{Number.isInteger(Number(item.line)) ? item.line : 'Import'}</td>
                                                    <td className="text-danger">{item.message}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={hideModal} disabled={isUploading}>Close</Button>
                    <Button variant="primary" className="vt-primary-btn border-0 text-white" onClick={handleUpload} disabled={isUploading || !selectedFile}>
                        {isUploading ? 'Uploading...' : (uploadError || importResult?.errors?.length ? 'Retry Upload' : 'Upload & Import')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default UploadModal;
